import { Box, darken, Stack, SxProps, Theme, Typography } from '@mui/material';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import { XIcon } from '@/components/icons/XIcon';
import { FONT_SIZING } from '@/styles/tokens/fonts';

import {
  NOTIFICATION_THEME_BY_SEVERITY,
  NotificationSeverity,
} from '../constants';

export type AlertProps = React.PropsWithChildren<{
  severity: NotificationSeverity;
  transparent?: boolean;
  sx?: SxProps<Theme>;
  handleDismiss?: () => void;
  // if present, shows a button the opens the target URL in the
  // current tab and hides the dismiss button
  alertTargetUrl?: string;
  // if present, shows a button that calls the callback when clicked
  alertActionButton?: {
    label: string;
    // Partial because Alert supplies sensible button defaults
    // (`variant="primary"`, `size="xs"`) before spreading.
    buttonProps?: Partial<ButtonProps>;
  };
}>;

function isExternalUrl(url: string): boolean {
  return url.startsWith('http');
}

const ALERT_PADDING = {
  top: 1,
  bottom: 1,
  left: 1.5,
  right: 1,
};

export const Alert = React.forwardRef(
  (
    {
      severity,
      children,
      transparent,
      handleDismiss,
      alertTargetUrl,
      alertActionButton,
      sx = {},
    }: AlertProps,
    ref
  ) => {
    const navigate = useNavigate();
    const theme = NOTIFICATION_THEME_BY_SEVERITY[severity];
    const borderWidth = 8;
    const themeStyleProps = {
      borderLeft: `solid ${borderWidth}px ${theme.border}`,
      bgcolor: transparent ? 'transparent' : theme.background,
      borderRadius: '4px',
    };

    return (
      <Box
        ref={ref}
        sx={{
          pt: ALERT_PADDING.top,
          pb: ALERT_PADDING.bottom,
          pl: ALERT_PADDING.left,
          pr: ALERT_PADDING.right,
          ...themeStyleProps,
          ...sx,
        }}
      >
        <Stack
          gap={1}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            fontSize={FONT_SIZING.xs}
            component="div"
            variant="body-xs"
            color={theme.text}
          >
            {children}
          </Typography>
          <Stack direction="row" gap={1} alignItems="center">
            {alertTargetUrl && (
              <Button
                variant="success-primary"
                size="xs"
                onClick={() => {
                  if (isExternalUrl(alertTargetUrl)) {
                    window.open(alertTargetUrl, '_blank');
                  } else {
                    navigate(alertTargetUrl);
                  }
                  handleDismiss?.();
                }}
              >
                View
              </Button>
            )}
            {alertActionButton && (
              <Button
                variant="primary"
                size="xs"
                {...alertActionButton.buttonProps}
                onClick={(e) => {
                  alertActionButton.buttonProps?.onClick?.(e);
                  handleDismiss?.();
                }}
              >
                {alertActionButton.label}
              </Button>
            )}
            {handleDismiss && !alertTargetUrl && (
              <Button
                variant="transparent"
                size="xs"
                square
                onClick={handleDismiss}
                sx={{
                  border: 'none',
                  '&:hover': {
                    backgroundColor: darken(theme.background, 0.05),
                    border: 'none',
                  },
                }}
              >
                <XIcon sx={{ color: theme.text }} />
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    );
  }
);

Alert.displayName = 'Alert';
